import axios from "axios";
import Vue from "vue";
export default {
  namespaced: true,
  state: {
    orders: [],
    loading: false,
    // Managed, colour-coded job-type catalog (field_job_order_types)
    job_types: [],
    // Map of orderId -> logs array (status change history)
    order_logs: {},

    // Notification state (2-minute polling)
    fjo_notifications: [],
    show_notification_dialog: false,
    last_notification_check: null,
    // Keys of dismissed notifications to suppress re-display.
    // Each key is "orderId_updatedAt" so re-edits produce a new key.
    dismissed_notification_keys: [],
  },
  getters: {
    orders(state) {
      return state.orders;
    },
    loading(state) {
      return state.loading;
    },
    order_logs(state) {
      return state.order_logs;
    },
    job_types(state) {
      return state.job_types;
    },
    status_counts(state) {
      var counts = { pending: 0, ongoing: 0, completed: 0, cancelled: 0 };
      state.orders.forEach((o) => {
        if (counts[o.status] !== undefined) counts[o.status]++;
      });
      return counts;
    },
    fjo_notifications(state) {
      return state.fjo_notifications;
    },
    show_notification_dialog(state) {
      return state.show_notification_dialog;
    },
    last_notification_check(state) {
      return state.last_notification_check;
    },
  },
  mutations: {
    SET_ORDERS(state, orders) {
      state.orders = orders;
    },
    ADD_ORDER(state, order) {
      state.orders.unshift(order);
    },
    EDIT_ORDER(state, order) {
      var index = state.orders.findIndex((a) => a.id == order.id);
      if (index !== -1) {
        state.orders.splice(index, 1, order);
      }
    },
    REMOVE_ORDER(state, id) {
      state.orders = state.orders.filter((o) => o.id != id);
    },
    SET_LOADING(state, val) {
      state.loading = val;
    },
    SET_JOB_TYPES(state, types) {
      state.job_types = types;
    },
    SET_ORDER_LOGS(state, { orderId, logs }) {
      Vue.set(state.order_logs, orderId, logs);
    },

    // ---- Notification mutations ----
    SET_FJO_NOTIFICATIONS(state, orders) {
      state.fjo_notifications = orders;
    },
    SET_SHOW_NOTIFICATION_DIALOG(state, val) {
      state.show_notification_dialog = val;
    },
    SET_LAST_NOTIFICATION_CHECK(state, timestamp) {
      state.last_notification_check = timestamp;
    },
    ADD_DISMISSED_NOTIFICATION_KEYS(state, keys) {
      // Merge new keys into the dismissed list, avoid duplicates
      keys.forEach((key) => {
        if (state.dismissed_notification_keys.indexOf(key) === -1) {
          state.dismissed_notification_keys.push(key);
        }
      });
      // Cap at 500 entries to prevent unbounded growth over long sessions
      if (state.dismissed_notification_keys.length > 500) {
        state.dismissed_notification_keys =
          state.dismissed_notification_keys.slice(-500);
      }
    },
    CLEAR_NOTIFICATION_STATE(state) {
      state.fjo_notifications = [];
      state.show_notification_dialog = false;
      state.last_notification_check = null;
      state.dismissed_notification_keys = [];
    },
  },
  actions: {
    async fetch_orders({ commit }, filters) {
      commit("SET_LOADING", true);
      try {
        let params = filters || {};
        let response = await axios.get("/field_job_orders", { params });
        commit("SET_ORDERS", response.data);
      } finally {
        commit("SET_LOADING", false);
      }
    },
    async create_order({ commit }, payload) {
      let response = await axios.post("/field_job_order", payload);
      commit("ADD_ORDER", response.data);
      return response.data;
    },
    async update_order({ commit }, payload) {
      let response = await axios.put("/field_job_order", payload);
      commit("EDIT_ORDER", response.data);
      return response.data;
    },
    async update_order_status({ commit }, { id, status, remark }) {
      let response = await axios.put("/field_job_order/" + id + "/status", {
        status,
        remark,
      });
      commit("EDIT_ORDER", response.data);
      return response.data;
    },
    async reschedule_order({ commit }, { id, scheduled_date, remark }) {
      let response = await axios.put("/field_job_order/" + id + "/reschedule", {
        scheduled_date,
        remark,
      });
      commit("EDIT_ORDER", response.data);
      return response.data;
    },
    async fetch_order_logs({ commit }, orderId) {
      let response = await axios.get("/field_job_order/" + orderId + "/logs");
      commit("SET_ORDER_LOGS", { orderId, logs: response.data });
      return response.data;
    },
    async delete_order({ commit }, id) {
      await axios.delete("/field_job_order/" + id);
      commit("REMOVE_ORDER", id);
    },

    // ---- Job type catalog (colour-coded, managed) ----
    async fetch_job_types({ commit }, all) {
      // Pass all=1 to include inactive types (manage dialog needs them).
      let params = all ? { all: 1 } : {};
      let response = await axios.get("/field_job_order_types", { params });
      commit("SET_JOB_TYPES", response.data);
      return response.data;
    },
    async create_job_type(ctx, payload) {
      let response = await axios.post("/field_job_order_type", payload);
      return response.data;
    },
    async update_job_type(ctx, payload) {
      let response = await axios.put("/field_job_order_type", payload);
      return response.data;
    },
    async delete_job_type(ctx, id) {
      await axios.delete("/field_job_order_type/" + id);
    },

    // ---- Notification actions (2-minute polling) ----

    /**
     * Fetch field job order notifications from the server since last check.
     * Filters out already-dismissed notifications and triggers the dialog
     * only if there are genuinely new/unseen notifications.
     */
    async fetch_fjo_notifications({ commit, state }) {
      const since = state.last_notification_check || new Date().toISOString();

      let response = await axios.get("/field_job_orders/notifications", {
        params: { since },
      });
      const { orders, checked_at } = response.data;

      const dismissed = state.dismissed_notification_keys;

      const newOrders = (orders || []).filter((o) => {
        var key = o.id + "_" + o.updated_at;
        return dismissed.indexOf(key) === -1;
      });

      commit("SET_LAST_NOTIFICATION_CHECK", checked_at);

      if (newOrders.length > 0) {
        commit("SET_FJO_NOTIFICATIONS", newOrders);
        commit("SET_SHOW_NOTIFICATION_DIALOG", true);
      }

      return newOrders;
    },

    /**
     * Dismiss the notification dialog and mark all currently displayed
     * notifications as dismissed so they will not re-appear.
     */
    dismiss_fjo_notifications({ commit, state }) {
      var keys = state.fjo_notifications.map((o) => o.id + "_" + o.updated_at);
      commit("ADD_DISMISSED_NOTIFICATION_KEYS", keys);
      commit("SET_FJO_NOTIFICATIONS", []);
      commit("SET_SHOW_NOTIFICATION_DIALOG", false);
    },

    /**
     * Clear all notification state. Called on logout.
     */
    clear_notification_state({ commit }) {
      commit("CLEAR_NOTIFICATION_STATE");
    },
  },
};
